import { parseDashboardURL } from "./read_state.js";

const dashboardSidebarHiddenStorageKey = "dashboard-sidebar-hidden";
const dashboardSidebarNextScrollStorageKey = "sidebar-scroll:next";
const dashboardSidebarSkipAutoFocusStorageKey = "sidebar-scroll:skip-autofocus";

function navKeyFromURL(urlObj) {
  if (!urlObj) {
    return "";
  }
  const pathname = String(urlObj.pathname || "").trim().replace(/\/+$/, "") || "/";
  const search = String(urlObj.search || "");
  return pathname + search;
}

function navKeyFromHref(href) {
  const urlObj = parseDashboardURL(String(href || "").trim());
  return navKeyFromURL(urlObj);
}

function readDashboardSidebarHiddenState() {
  try {
    return window.localStorage.getItem(dashboardSidebarHiddenStorageKey) === "1";
  } catch (_err) {
    return false;
  }
}

function writeDashboardSidebarHiddenState(isHidden) {
  try {
    window.localStorage.setItem(dashboardSidebarHiddenStorageKey, isHidden ? "1" : "0");
  } catch (_err) {
    // Ignore storage failures.
  }
}

function applyDashboardSidebarHiddenState(isHidden) {
  const entriesWrap = document.querySelector(".dashboard-entries-wrap");
  if (entriesWrap instanceof HTMLElement) {
    entriesWrap.classList.toggle("is-sidebar-hidden", isHidden);
  }

  const toggleButton = document.getElementById("dashboard-sidebar-toggle");
  if (!(toggleButton instanceof HTMLButtonElement)) {
    return;
  }

  toggleButton.setAttribute("aria-expanded", isHidden ? "false" : "true");
  const label = toggleButton.querySelector("[data-dashboard-sidebar-toggle-label]");
  if (label instanceof HTMLElement) {
    label.textContent = isHidden ? "Show feed list" : "Hide feed list";
  }
}

export function initDashboardSidebarToggle() {
  const toggleButton = document.getElementById("dashboard-sidebar-toggle");
  if (!(toggleButton instanceof HTMLButtonElement)) {
    return;
  }

  if (toggleButton.dataset.sidebarToggleBound !== "1") {
    toggleButton.dataset.sidebarToggleBound = "1";
    toggleButton.addEventListener("click", function () {
      const entriesWrap = document.querySelector(".dashboard-entries-wrap");
      const isHidden = entriesWrap instanceof HTMLElement
        ? !entriesWrap.classList.contains("is-sidebar-hidden")
        : false;

      applyDashboardSidebarHiddenState(isHidden);
      writeDashboardSidebarHiddenState(isHidden);
    });
  }

  applyDashboardSidebarHiddenState(readDashboardSidebarHiddenState());
}

export function syncSidebarActiveState() {
  const sidebar = document.querySelector(".entries-sidebar");
  if (!(sidebar instanceof HTMLElement)) {
    return;
  }

  const currentKey = navKeyFromURL(parseDashboardURL(window.location.pathname + window.location.search));
  if (!currentKey) {
    return;
  }

  sidebar.querySelectorAll(".sidebar-feed-row").forEach(function (row) {
    if (!(row instanceof HTMLElement)) {
      return;
    }
    const link = row.querySelector(".sidebar-feed-link[href]");
    const isActive = link instanceof HTMLAnchorElement && navKeyFromHref(link.getAttribute("href")) === currentKey;
    row.classList.toggle("is-active", isActive);
  });

  sidebar.querySelectorAll(".feed-section-title-link[href]").forEach(function (link) {
    if (!(link instanceof HTMLAnchorElement)) {
      return;
    }
    link.classList.toggle("is-active", navKeyFromHref(link.getAttribute("href")) === currentKey);
  });

  sidebar.querySelectorAll(".feed-section").forEach(function (section) {
    if (!(section instanceof HTMLElement)) {
      return;
    }
    const header = section.querySelector(".feed-section-header");
    if (!(header instanceof HTMLElement)) {
      return;
    }

    const sectionLink = section.querySelector(".feed-section-title-link.is-active");
    const activeRow = section.querySelector(".sidebar-feed-row.is-active");
    header.classList.toggle("is-active", Boolean(sectionLink) || Boolean(activeRow));
  });
}

export function highlightReaderContent(root) {
  if (!window.hljs) {
    return;
  }

  const scope = root instanceof Element || root instanceof Document ? root : document;
  const blocks = scope.querySelectorAll(".reader-content pre code");
  blocks.forEach(function (codeEl) {
    if (!(codeEl instanceof HTMLElement)) {
      return;
    }
    if (codeEl.classList.contains("hljs")) {
      return;
    }
    window.hljs.highlightElement(codeEl);
  });
}

function sidebarScrollStorageKey() {
  return "sidebar-scroll:" + window.location.pathname;
}

export function saveSidebarScrollPosition() {
  const sidebar = document.querySelector(".entries-sidebar");
  if (!(sidebar instanceof HTMLElement)) {
    return;
  }
  try {
    window.sessionStorage.setItem(sidebarScrollStorageKey(), String(sidebar.scrollTop));
  } catch (_err) {
    // Ignore storage failures.
  }
}

export function restoreSidebarScrollPosition() {
  const sidebar = document.querySelector(".entries-sidebar");
  if (!(sidebar instanceof HTMLElement)) {
    return;
  }
  try {
    let raw = window.sessionStorage.getItem(dashboardSidebarNextScrollStorageKey);
    if (raw) {
      window.sessionStorage.removeItem(dashboardSidebarNextScrollStorageKey);
    } else {
      raw = window.sessionStorage.getItem(sidebarScrollStorageKey());
    }
    if (!raw) {
      return;
    }
    const value = Number(raw);
    if (!Number.isFinite(value) || value < 0) {
      return;
    }
    sidebar.scrollTop = value;
  } catch (_err) {
    // Ignore storage failures.
  }
}

export function preserveSidebarScrollForNextNavigation() {
  const sidebar = document.querySelector(".entries-sidebar");
  if (!(sidebar instanceof HTMLElement)) {
    return;
  }
  try {
    window.sessionStorage.setItem(dashboardSidebarNextScrollStorageKey, String(sidebar.scrollTop));
    window.sessionStorage.setItem(dashboardSidebarSkipAutoFocusStorageKey, "1");
  } catch (_err) {
    // Ignore storage failures.
  }
}

export function attachSidebarScrollPersistence() {
  const sidebar = document.querySelector(".entries-sidebar");
  if (!(sidebar instanceof HTMLElement)) {
    return;
  }
  if (sidebar.dataset.scrollPersistenceAttached === "1") {
    return;
  }
  sidebar.dataset.scrollPersistenceAttached = "1";
  sidebar.addEventListener("scroll", function () {
    saveSidebarScrollPosition();
  }, { passive: true });
}

export function focusActiveFeedInSidebar() {
  try {
    if (window.sessionStorage.getItem(dashboardSidebarSkipAutoFocusStorageKey) === "1") {
      window.sessionStorage.removeItem(dashboardSidebarSkipAutoFocusStorageKey);
      return;
    }
  } catch (_err) {
    // Ignore storage failures.
  }

  const sidebar = document.querySelector(".entries-sidebar");
  if (!(sidebar instanceof HTMLElement)) {
    return;
  }

  const activeRow = sidebar.querySelector(".sidebar-feed-row.is-active");
  if (!(activeRow instanceof HTMLElement)) {
    return;
  }

  const section = activeRow.closest(".feed-section");
  if (section && section.classList.contains("is-collapsed")) {
    section.classList.remove("is-collapsed");
    const toggle = section.querySelector(".feed-section-arrow-toggle");
    if (toggle instanceof HTMLElement) {
      toggle.setAttribute("aria-expanded", "true");
      const form = toggle.closest("form");
      if (form instanceof HTMLFormElement) {
        const collapsedInput = form.querySelector('input[name="collapsed"]');
        if (collapsedInput instanceof HTMLInputElement) {
          collapsedInput.value = "1";
        }
      }
    }
  }

  window.requestAnimationFrame(function () {
    activeRow.scrollIntoView({ block: "center", inline: "nearest" });
    saveSidebarScrollPosition();
  });
}

export function closeFeedMenuForElement(elt) {
  if (!(elt instanceof Element)) {
    return;
  }
  const menu = elt.closest("details.sidebar-feed-menu");
  if (!(menu instanceof HTMLDetailsElement)) {
    return;
  }
  menu.removeAttribute("open");
}

export function isOverlayElement(elt) {
  if (!(elt instanceof HTMLElement)) {
    return false;
  }
  return elt.id === "entry-overlay" || elt.id === "feed-overlay";
}

function syncPageScrollLockForOverlays() {
  const hasOpenOverlay = Array.from(document.querySelectorAll("#entry-overlay, #feed-overlay")).some(function (overlayEl) {
    if (!(overlayEl instanceof HTMLElement)) {
      return false;
    }
    return overlayEl.classList.contains("is-open") && overlayEl.innerHTML.trim().length > 0;
  });

  document.documentElement.classList.toggle("page-overlay-open", hasOpenOverlay);
  if (document.body) {
    document.body.classList.toggle("page-overlay-open", hasOpenOverlay);
  }
}

export function setOverlayOpenState(overlayEl, isOpen) {
  if (!isOverlayElement(overlayEl)) {
    return;
  }
  if (isOpen) {
    overlayEl.classList.add("is-open");
    overlayEl.setAttribute("aria-hidden", "false");
    syncPageScrollLockForOverlays();
    return;
  }
  overlayEl.classList.remove("is-open");
  overlayEl.setAttribute("aria-hidden", "true");
  syncPageScrollLockForOverlays();
}

export function closeOverlayElement(overlayEl) {
  if (!isOverlayElement(overlayEl)) {
    return;
  }
  overlayEl.innerHTML = "";
  setOverlayOpenState(overlayEl, false);
}

export function closeAllOpenOverlays() {
  document.querySelectorAll("#entry-overlay, #feed-overlay").forEach(function (overlayEl) {
    if (!(overlayEl instanceof HTMLElement)) {
      return;
    }
    if (overlayEl.innerHTML.trim().length === 0) {
      setOverlayOpenState(overlayEl, false);
      return;
    }
    closeOverlayElement(overlayEl);
  });
  syncPageScrollLockForOverlays();
}

export function syncEntriesAutoRefreshPath() {
  if (!window.htmx) {
    return;
  }
  const autoRefreshEl = document.getElementById("entries-auto-refresh");
  if (!autoRefreshEl) {
    return;
  }

  const currentPath = window.location.pathname + window.location.search;
  if (autoRefreshEl.getAttribute("hx-get") === currentPath) {
    return;
  }

  autoRefreshEl.setAttribute("hx-get", currentPath);
  window.htmx.process(autoRefreshEl);
}

export function autoReloadEmptyFeedOnce() {
  if (!window.htmx) {
    return;
  }

  const entriesContainer = document.getElementById("entries-content");
  if (!entriesContainer) {
    return;
  }

  const reloadForm = entriesContainer.querySelector("form[data-auto-empty-feed-reload='1']");
  if (!reloadForm) {
    return;
  }

  const pageKey = window.location.pathname + window.location.search;
  const storageKey = "auto-empty-feed-reload:" + pageKey;
  let alreadyAttempted = false;
  try {
    alreadyAttempted = window.sessionStorage.getItem(storageKey) === "1";
  } catch (_err) {
    alreadyAttempted = false;
  }
  if (alreadyAttempted) {
    return;
  }

  try {
    window.sessionStorage.setItem(storageKey, "1");
  } catch (_err) {
    // Ignore storage issues.
  }

  window.htmx.trigger(reloadForm, "submit");
}
